import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import BookCardHeader from "./BookCardHeader";
import BookCardField from "./BookCardField";
import LoanTable from "./LoanTable";
import { useBookCardData } from "../hooks/useBookCardData";
import {
  CONSTANTS,
  filterLoanHistoryByItem,
  getFrontCardLoanRecords,
} from "../utils/bookCardUtils";

const STYLES = {
  container: {
    backgroundColor: "white",
    border: "2px solid #333",
    padding: "0px",
    borderRadius: "6px",
    width: "500px",
    height: "720px",
    margin: "-6px auto",
    fontFamily: "Arial, sans-serif",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
};

const FormDataPropType = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  title: PropTypes.string,
  author: PropTypes.string,
  checkOutDate: PropTypes.string,
  dueDate: PropTypes.string,
  document: PropTypes.object,
  patronName: PropTypes.string,
  hasActiveLoan: PropTypes.bool,
  hasRealData: PropTypes.bool,
});

const DocumentPropType = PropTypes.shape({
  metadata: PropTypes.shape({
    authors: PropTypes.array,
    other_authors: PropTypes.bool,
    title: PropTypes.string,
    call_number: PropTypes.string,
    barcode: PropTypes.string,
    identifiers: PropTypes.array,
    circulation: PropTypes.object,
    items: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  }),
});

const LoanHistoryPropType = PropTypes.arrayOf(
  PropTypes.shape({
    patron: PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      full_name: PropTypes.string,
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      program: PropTypes.string,
    }),
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    checkout_date: PropTypes.string,
    due_date: PropTypes.string,
    return_date: PropTypes.string,
    item_pid: PropTypes.string,
    item: PropTypes.object,
  })
);

const BookCardFront = ({
  formData,
  document,
  loanHistory = [],
  selectedItemId,
  onItemChange,
  availableItems,
  currentBookCard = 0,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Reset pagination when item changes or book card changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedItemId, currentBookCard]);

  // Filter loan history for selected item using utility function
  const filteredLoanHistory = filterLoanHistoryByItem(
    loanHistory,
    selectedItemId
  );

  // Get loan records for THIS book card's front side only
  const frontCardLoanHistory = getFrontCardLoanRecords(
    filteredLoanHistory,
    currentBookCard
  );

  const currentItem =
    availableItems.find((item) => item.id === selectedItemId) ||
    availableItems[0];

  // Pass the sliced loan history to useBookCardData hook
  const {
    authors,
    hasOtherAuthors,
    title,
    loanRecords,
    callNumber,
    callNumberPrefix,
    baseCallNumber,
    callNumberSuffix,
    barcode,
  } = useBookCardData(formData, document, frontCardLoanHistory, currentItem);

  const totalPages =
    Math.ceil(loanRecords.length / CONSTANTS.RECORDS_PER_PAGE) || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div style={STYLES.container}>
      <BookCardHeader />

      <BookCardField
        title={title}
        authors={authors}
        hasOtherAuthors={hasOtherAuthors}
        callNumber={callNumber}
        callNumberPrefix={callNumberPrefix}
        baseCallNumber={baseCallNumber}
        callNumberSuffix={callNumberSuffix}
        barcode={barcode}
        formData={formData}
      >
        <LoanTable
          loanRecords={loanRecords}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </BookCardField>
    </div>
  );
};

BookCardFront.propTypes = {
  formData: FormDataPropType.isRequired,
  document: DocumentPropType,
  loanHistory: LoanHistoryPropType,
  selectedItemId: PropTypes.string,
  onItemChange: PropTypes.func,
  availableItems: PropTypes.array,
  currentBookCard: PropTypes.number,
};

export default BookCardFront;
